import { z } from 'zod';
import type { DealListParams, DealType } from './service/types';

const DEAL_STATUS_OPTIONS = ['active', 'paused', 'expired', 'cancelled'] as const;
const DEAL_TYPE_OPTIONS: DealType[] = ['percent_off', 'fixed_price', 'bundle'];

export const dealsSearchSchema = z.object({
  status: z.string().optional(),
  branch: z.string().optional(),
  deal_type: z.string().optional(),
});

export type DealsSearchInput = z.infer<typeof dealsSearchSchema>;

export interface DealsRouteSearch {
  status?: string;
  branch?: string;
  deal_type?: string;
}

function normalizeStatus(value: DealsSearchInput['status']): DealListParams['status'] | undefined {
  if (typeof value !== 'string') return undefined;
  const normalized = value.trim().toLowerCase();
  if (!normalized || normalized === 'all') return undefined;
  if (!DEAL_STATUS_OPTIONS.includes(normalized as (typeof DEAL_STATUS_OPTIONS)[number])) {
    return undefined;
  }
  return normalized as DealListParams['status'];
}

function normalizeBranch(value: DealsSearchInput['branch']): string | undefined {
  if (typeof value !== 'string') return undefined;
  const normalized = value.trim();
  if (!normalized || normalized === 'all') return undefined;
  return normalized;
}

function normalizeDealType(value: DealsSearchInput['deal_type']): DealType | undefined {
  if (typeof value !== 'string') return undefined;
  const normalized = value.trim().toLowerCase();
  if (!normalized || normalized === 'all') return undefined;
  if (!DEAL_TYPE_OPTIONS.includes(normalized as DealType)) return undefined;
  return normalized as DealType;
}

export function normalizeDealsSearch(search: DealsSearchInput): DealsRouteSearch {
  return {
    status: normalizeStatus(search.status),
    branch: normalizeBranch(search.branch),
    deal_type: normalizeDealType(search.deal_type),
  };
}

export function areDealsSearchEqual(a: DealsSearchInput, b: DealsRouteSearch): boolean {
  return a.status === b.status && a.branch === b.branch && a.deal_type === b.deal_type;
}

export function dealsLoaderDeps(search: DealsSearchInput): DealListParams {
  const normalized = normalizeDealsSearch(search);
  return {
    status: normalizeStatus(normalized.status),
    branch_id: normalizeBranch(normalized.branch),
    deal_type: normalizeDealType(normalized.deal_type),
  };
}

export { DEAL_STATUS_OPTIONS, DEAL_TYPE_OPTIONS };
